import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Pause, Play, SkipForward } from 'lucide-react'
import { Button } from '../ui/button'
import { useTimerStore } from '../../stores/timer-store'

export function TimerNavControls() {
  const { t } = useTranslation()
  const paused = useTimerStore((s) => s.paused)
  const templates = useTimerStore((s) => s.templates)
  const templateIdx = useTimerStore((s) => s.templateIdx)
  const phase = useTimerStore((s) => s.phase)
  const togglePause = useTimerStore((s) => s.togglePause)
  const advanceRep = useTimerStore((s) => s.advanceRep)
  const rewindRep = useTimerStore((s) => s.rewindRep)
  const advanceSet = useTimerStore((s) => s.advanceSet)
  const rewindSet = useTimerStore((s) => s.rewindSet)
  const manualAdvance = useTimerStore((s) => s.manualAdvance)
  const startTimer = useTimerStore((s) => s.startTimer)

  const tpl = templates[templateIdx]
  const showRepButtons = (tpl?.reps_per_set ?? 1) > 1
  const navDisabled = phase === 'idle' || phase === 'armed' || phase === 'prep' || phase === 'completed'
  const isManualTemplate = tpl?.manual === true
  const isManualWork = phase === 'work' && isManualTemplate
  const isManualRest = (phase === 'rest' || phase === 'set_rest') && isManualTemplate
  const isBetweenExercises = phase === 'between_exercises'

  const primaryLabel = (() => {
    if (phase === 'prep') return t('workoutTimer.controls.preparing', 'Preparing…')
    if (isBetweenExercises) return t('workoutTimer.controls.startNext', 'Next')
    if (isManualWork) return t('workoutTimer.controls.done', 'Done')
    if (isManualRest) return t('workoutTimer.controls.skipRest', 'Skip rest')
    return paused ? t('workoutTimer.controls.resume', 'Resume') : t('workoutTimer.controls.pause', 'Pause')
  })()

  const PrimaryIcon = (() => {
    if (isManualRest) return SkipForward
    if (isManualWork) return null
    if (isBetweenExercises) return Play
    return paused ? Play : Pause
  })()

  function handlePrimary() {
    if (isBetweenExercises) {
      startTimer()
    } else if (isManualWork || isManualRest) {
      manualAdvance()
    } else {
      togglePause()
    }
  }

  return (
    <div className="flex items-center justify-between gap-1 w-full">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        disabled={navDisabled}
        onClick={rewindSet}
        aria-label={t('workoutTimer.controls.prevSet', 'Previous set')}
      >
        <ChevronsLeft className="size-5" />
      </Button>
      {showRepButtons && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={navDisabled}
          onClick={rewindRep}
          aria-label={t('workoutTimer.controls.prevRep', 'Previous rep')}
        >
          <ChevronLeft className="size-5" />
        </Button>
      )}
      <Button
        type="button"
        size="lg"
        disabled={phase === 'prep' || phase === 'idle' || phase === 'armed' || phase === 'completed'}
        onClick={handlePrimary}
        className="flex-1 mx-1"
        aria-label={primaryLabel}
      >
        {PrimaryIcon && <PrimaryIcon className="size-5 mr-1" />}
        {primaryLabel}
      </Button>
      {showRepButtons && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={navDisabled}
          onClick={advanceRep}
          aria-label={t('workoutTimer.controls.nextRep', 'Next rep')}
        >
          <ChevronRight className="size-5" />
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        disabled={navDisabled}
        onClick={advanceSet}
        aria-label={t('workoutTimer.controls.nextSet', 'Next set')}
      >
        <ChevronsRight className="size-5" />
      </Button>
    </div>
  )
}
